import { auroraDrift } from "./aurora-drift";
import { midnightVelvet } from "./midnight-velvet";
import { prismCandyland } from "./prism-candyland";
import { rainbowRoad } from "./rainbow-road";
import { stardustRainbowRoad } from "./stardust-rainbow-road";
import type { ThemeConfig, ThemeTokens } from "./types";
import { unicornLand } from "./unicorn-land";

const themes: ThemeConfig[] = [prismCandyland, rainbowRoad, stardustRainbowRoad, unicornLand, auroraDrift, midnightVelvet];

const themeIndex: Record<string, ThemeConfig> = Object.fromEntries(
  themes.map((theme) => [theme.id, theme])
);

export const DEFAULT_THEME_ID = "prism-candyland";

export function listThemes(): ThemeConfig[] {
  return themes.slice();
}

export function getTheme(id: string): ThemeConfig {
  return themeIndex[id] ?? themeIndex[DEFAULT_THEME_ID]!;
}

export function isKnownTheme(id: string): boolean {
  return Object.prototype.hasOwnProperty.call(themeIndex, id);
}

// Token-name map: every CSS variable consumed by the app maps back to a key
// in `ThemeTokens`. Adding a new token = updating the contract in one place.
const cssVarNames: Record<keyof ThemeTokens, string> = {
  bg: "--color-bg",
  bgElevated: "--color-bg-elevated",
  bgPanel: "--color-bg-panel",
  bgOverlay: "--color-bg-overlay",
  fg: "--color-fg",
  fgMuted: "--color-fg-muted",
  fgStrong: "--color-fg-strong",
  fgInverse: "--color-fg-inverse",
  accent: "--color-accent",
  accentSoft: "--color-accent-soft",
  accent2: "--color-accent-2",
  accent2Soft: "--color-accent-2-soft",
  accentRainbow: "--color-accent-rainbow",
  border: "--color-border",
  borderSoft: "--color-border-soft",
  borderStrong: "--color-border-strong",
  positive: "--color-positive",
  negative: "--color-negative",
  warning: "--color-warning",
  bankPanelBg: "--color-bank-panel-bg",
  bankPanelBorder: "--color-bank-panel-border",
  bankPanelHeading: "--color-bank-panel-heading",
  fontDisplay: "--font-display",
  fontBody: "--font-body",
  fontMono: "--font-mono",
  decorationGlow: "--decoration-glow"
};

export function applyThemeToRoot(theme: ThemeConfig, root: HTMLElement): void {
  for (const key of Object.keys(cssVarNames) as (keyof ThemeTokens)[]) {
    root.style.setProperty(cssVarNames[key], theme.tokens[key]);
  }
  root.dataset["theme"] = theme.id;
}

export type { ThemeConfig, ThemeTokens } from "./types";
